import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { announcementAPI } from '../../services/api';
import { FaArrowLeft, FaBullhorn } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Button from '../../components/Button';

const PostAnnouncement = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        type: 'general',
        priority: 'normal',
        target_audience: [], // Array of roles: ['student', 'alumni', 'member', etc.]
        is_pinned: false,
        expires_at: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Map priority to type and ensure target_audience is properly formatted
            const submitData = {
                title: formData.title,
                content: formData.content,
                type: formData.type || 'general',
                target_audience: formData.target_audience.length > 0 ? formData.target_audience : [], // Empty array means visible to all
                is_pinned: formData.is_pinned,
                expires_at: formData.expires_at || null
            };
            await announcementAPI.create(submitData);
            toast.success('Announcement posted successfully!');
            navigate('/announcements');
        } catch (err) {
            console.error('Announcement posting error:', err);
            toast.error(err.message || 'Failed to post announcement. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-4xl mx-auto px-6 py-8">
                <Link to="/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                    <FaArrowLeft className="mr-2" /> Back to Dashboard
                </Link>

                <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <FaBullhorn className="text-3xl text-primary-600" />
                        <h1 className="text-3xl font-bold text-gray-900">Post Announcement</h1>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Title *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.title}
                                onChange={(e) => setFormData({...formData, title: e.target.value})}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                                placeholder="Enter announcement title"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Content *
                            </label>
                            <textarea
                                required
                                rows={8}
                                value={formData.content}
                                onChange={(e) => setFormData({...formData, content: e.target.value})}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                                placeholder="Write your announcement content here..."
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                                >
                                    <option value="general">General</option>
                                    <option value="important">Important</option>
                                    <option value="urgent">Urgent</option>
                                    <option value="event">Event</option>
                                    <option value="scholarship">Scholarship</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Expires At (Optional)
                                </label>
                                <input
                                    type="datetime-local"
                                    value={formData.expires_at}
                                    onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Target Audience (Leave empty for all users)
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {['student', 'alumni', 'member', 'applicant', 'employer'].map(role => (
                                    <label key={role} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={formData.target_audience.includes(role)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setFormData({
                                                        ...formData,
                                                        target_audience: [...formData.target_audience, role]
                                                    });
                                                } else {
                                                    setFormData({
                                                        ...formData,
                                                        target_audience: formData.target_audience.filter(r => r !== role)
                                                    });
                                                }
                                            }}
                                            className="h-4 w-4 text-primary-600 focus:ring-primary-600 border-gray-300 rounded"
                                        />
                                        <span className="ml-2 text-sm text-gray-700 capitalize">{role}</span>
                                    </label>
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                                If no roles are selected, the announcement will be visible to all users.
                            </p>
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="is_pinned"
                                checked={formData.is_pinned}
                                onChange={(e) => setFormData({...formData, is_pinned: e.target.checked})}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-600 border-gray-300 rounded"
                            />
                            <label htmlFor="is_pinned" className="ml-2 block text-sm text-gray-700">
                                Pin this announcement (show at top)
                            </label>
                        </div>

                        <div className="flex gap-4 pt-4">
                            <Button type="submit" variant="primary-gradient" className="flex-1 px-6 py-3">
                                {loading ? 'Posting...' : 'Post Announcement'}
                            </Button>
                            <button
                                type="button"
                                onClick={() => navigate('/announcements')}
                                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PostAnnouncement;

